import React from 'react';
import { useParams } from 'react-router-dom';

import axios from 'axios';
import { useQuery } from '@tanstack/react-query';

import Spinner from '../../../components/Spinner';
import ErrorBox from '../../../components/ErrorBox';
import LinearChart from '../ReputationScore/LinearChart';
import ProgressBar from '../../../components/ProgressBar';

import { lineChartTypes, NPSStatuses } from '../../../constants/Dashboard';

const evalChangeObj = value => {
  const changeObj = {};
  
  switch (true) {
    case value > 0:
      changeObj.status = 'increased';
      changeObj.value = `+${value}%`;
      changeObj.color = '#20c997';
      break;
    case value < 0:
      changeObj.status = 'decreased';
      changeObj.value = `${value}%`;
      changeObj.color = '#f44336';
      break;
    case value === 0:
    default:
      changeObj.status = 'same';
      changeObj.value = `${value}%`;
      changeObj.color = '#fa8c15';
      break;
  }
  
  return changeObj;
};

const evalScoreColors = score => {
  const colorsObj = {};
  
  switch (true) {
    case score <= 0:
      colorsObj.color = '#f44336';
      colorsObj.background = 'linear-gradient(0deg, rgba(244, 67, 54, 0.2) 0%, rgba(255, 255, 255, 0) 100%)';
      break;
    case score > 0 && score < 50:
      colorsObj.color = '#fa8c15';
      colorsObj.background = 'linear-gradient(0deg, rgba(255, 132, 0, 0.2) 0%, rgba(255, 255, 255, 0) 100%)';
      break;
    case score >= 50:
    default:
      colorsObj.color = '#20c997';
      colorsObj.background = 'linear-gradient(0deg, rgba(32, 201, 151, 0.2) 0%, rgba(255, 255, 255, 0) 100%)';
      break;
  }
  
  return colorsObj;
};

const fetchNPSScore = async ({ queryKey }) => {
  const [, locationId] = queryKey;
  
  const { data } = await axios.get(`${import.meta.env.VITE_API_URL}api/v4/locachecker/nps`, {
    params: { locationId },
  });
  
  return data.data;
};

const NetPromoterScore = () => {
  const { locationId } = useParams();
  
  const {
    data: netPromoterScore,
    isLoading,
    isError
  } = useQuery({
    queryKey: ['netPromoterScore', locationId],
    queryFn: fetchNPSScore,
    enabled: !!locationId,
  });
  
  if (isLoading) {
    return <Spinner height={635}/>;
  }
  
  if (isError) {
    return <ErrorBox description="We encountered an issue loading your NPS Score. Please try again."/>;
  }
  
  const changeObj = evalChangeObj(netPromoterScore?.NPSChange);
  const colorsObj = evalScoreColors(netPromoterScore?.NPS);
  
  return (
    <div
      className="flex items-center justify-between gap-x-[30px] rounded-md bg-white p-8 px-10 shadow-[0px_4px_8px_0px_#0000000D] max-[1550px]:flex-col max-[1550px]:items-start max-[1550px]:gap-y-[30px] min-[1800px]:gap-x-[60px]">
      <div>
        <h2 className="mb-3 text-2xl font-medium leading-[31.2px]">Net Promoter Score</h2>
        <span
          className="text-[#545454] text-[15px] font-normal leading-6 min-[2000px]:flex min-[2000px]:w-max">{NPSStatuses[changeObj.status]}</span>
      </div>
      
      <div
        className="flex w-full items-center justify-between gap-x-[30px] max-[1200px]:flex-wrap max-[1200px]:justify-center max-[1200px]:gap-[30px] min-[1800px]:gap-x-[60px]">
        <div
          className="flex h-[222px] w-full max-w-[222px] flex-col items-center justify-between gap-y-6 rounded-md border border-[#E4E4E4] p-[24.5px_22.5px]"
          style={{ background: colorsObj.background }}>
          <span
            className="relative m-0 text-lg font-medium leading-6 text-black">
            Net Promoter Score
          </span>
          
          <div className="relative">
            <span className="text-[64px] font-medium leading-[83.2px] tracking-[0.1166em]" style={{ color: colorsObj.color }}>
              {netPromoterScore?.NPS}
            </span>
            <span
              className="absolute -right-[30px] text-sm font-medium leading-[18px]" style={{ color: changeObj.color }}>
              {changeObj.value}
            </span>
          </div>
          
          <span className="text-sm font-medium leading-[18px] text-black">Industry average: {netPromoterScore?.industryNPS}</span>
        </div>
        
        <div className="flex w-[368px] flex-col gap-y-4 max-[1200px]:flex-grow min-[1800px]:w-full min-[1800px]:max-w-[500px]">
          <span className="text-xs font-medium leading-[14px] tracking-[0.1em]">SCORE</span>
          
          {netPromoterScore?.NPSCategoriesValues.map((category, i) => (
            <div key={i} className="flex flex-col">
              <span className="text-sm font-normal leading-[18px]">
                {category.type}: <span className="text-sm font-medium leading-[18px]">{category.value}</span>
              </span>
              
              <ProgressBar
                percent={category.percent}
                color={category.color}
              />
            </div>
          ))}
        </div>
        
        <LinearChart type={lineChartTypes.single} data={netPromoterScore?.NPSTrend}
                     className="max-w-[470px] max-[1200px]:max-w-none min-[2000px]:max-w-[800px]"/>
      </div>
    </div>
  );
};

export default NetPromoterScore;
